import type { MembershipMethod } from '@brush/api-client';

/**
 * The words the claim flow is allowed to say, in one file.
 *
 * **This is not tidiness — it is the control.** ADR 0010 Decision 3 makes the 202 uniform on
 * purpose: the server answers a claim identically whether the contact matched a card, matched
 * nothing, or matched a card somebody else already holds, because an endpoint that distinguished
 * them would let anybody test an address against a room's membership list. A screen can give that
 * away for free. "We've sent you a code" is a claim the server carefully declined to make, and it
 * would be made here, in a component nobody reviews for security properties.
 *
 * So every sentence below is conditional, and the condition is the whole of it. Read them as one
 * paragraph before changing any of them: what has to survive an edit is that a reader learns
 * nothing from the wording that they did not already type in.
 */

/**
 * After a claim starts. `destinationMasked` is safe to show because the server derives it from
 * what the caller typed rather than from what the room holds — deliberately, so that it is not the
 * one field on the response whose content depends on a match.
 */
export function codeSentNotice(roomName: string, destinationMasked: string): string {
  return `If ${roomName} has ${destinationMasked} on file for one of its cards, a 6-digit code has been sent to it.`;
}

/**
 * The second half, and it carries the three ordinary reasons no code arrived — stated together so
 * that none of them is an answer about this particular claim. A player who waits for a code that
 * was never sent, with nothing on screen explaining why one might not be, concludes the app is
 * broken; it is the room's contact record that is.
 */
export const NO_CODE_NOTICE =
  'Nothing will arrive if the room has a different address on file, has none at all, or that card is already connected to somebody. If you are not sure which, ask the room to check the contact details on your card.';

/** What the code screen says about giving up. */
export const START_AGAIN_HINT = 'Codes last ten minutes. Start again to get a new one.';

/**
 * What a failure on the completion endpoint means for the screen — three answers, because they
 * send a player to three different places and a single "that didn't work" would send them nowhere.
 *
 * The API's own message is what gets rendered in every case (it carries the attempts remaining,
 * which is deliberately not counted here — see `ConnectCard`); this only decides which controls
 * to leave on screen beside it.
 */
export type ClaimFailure =
  /** The code was wrong. Stay put; there are attempts left. */
  | 'retry'
  /** This challenge is finished — expired, spent, or out of attempts. A new code is the way on. */
  | 'restart'
  /**
   * No code will help. The card is somebody else's, or gone, or — the friendly member of this
   * group — already the caller's own, where the only thing left to do is go and look at it. All
   * four leave the same controls because they need the same one: a way back, not another code.
   */
  | 'stop';

export function claimFailureFor(code: string | undefined): ClaimFailure {
  switch (code) {
    case 'challenge_expired':
    case 'challenge_consumed':
    case 'too_many_attempts':
      return 'restart';
    case 'card_already_yours':
    case 'card_already_claimed':
    case 'already_linked_at_room':
    case 'membership_conflict':
    case 'player_not_found':
      return 'stop';
    default:
      // Including `invalid_code`, and including anything unrecognised: leaving the code box on
      // screen is the recoverable guess, and the alternative — throwing somebody back to the start
      // of a flow whose challenge is still live — spends one of the five sends they are allowed.
      return 'retry';
  }
}

/**
 * How a card came to be connected, in a player's terms.
 *
 * Deliberately not shared with room-web's memberships panel, which reads the same column for a
 * different reader: a player is being told how *they* proved it, an operator is being told what
 * evidence the room holds about somebody else. Same rule that keeps `LeaderboardBreakdown` in
 * this app rather than in `packages/ui`.
 */
export function methodLabel(method: MembershipMethod | string): string {
  switch (method) {
    case 'platform_otp_email':
      return 'Emailed code';
    case 'platform_otp_sms':
      return 'Texted code';
    case 'room_idp_attested':
    case 'room_idp_federated':
      return "The room's own sign-in";
    case 'room_api':
      return "The room's records";
    case 'verified_email_match':
      return 'Verified email address';
    default:
      // A method this build does not know about is still a real membership, and saying so beats
      // rendering a raw literal at somebody. Adding a value to the server's vocabulary needs no
      // migration, so this branch is reachable by a deploy order rather than by a bug.
      return 'Connected';
  }
}

/** `mm:ss` until the challenge expires, or null once it has. */
export function timeUntil(expiresAt: string, now: number): string | null {
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return null;

  const seconds = Math.floor(remaining / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
